import useLanguageNavigate from '@hooks/useLanguageNavigate';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Avatar, Box, Card, CardActionArea, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Request } from '../types';

interface RequestCardProps {
	request: Request;
	isSelected: boolean;
	onClick: (requestId: string, serviceId: string) => void;
	isMobile?: boolean;
}

export const RequestCard: React.FC<RequestCardProps> = ({
	request,
	isSelected,
	onClick,
	isMobile,
}) => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();

	const mainCrop = request.crops?.[0] || request.crop;
	const serviceId = request.services_id || request.service_id || '';

	const handleClick = () => {
		onClick(request.request_id, serviceId);
	};

	const navigateToDetailedPage = () => {
		navigateWithLanguage(`/farmer/express/offers/${request.id}`);
	};

	// Define a more noticeable selected state color
	const selectedBgColor = '#F5EFE9';
	const hoverBgColor = '#F8F4F0';

	return (
		<Card
			sx={{
				mb: 2,
				borderRadius: '8px',
				boxShadow: isSelected ? '0px 4px 8px rgba(0, 0, 0, 0.1)' : 'none',
				backgroundColor: isSelected ? selectedBgColor : 'white',
				transition: 'all 0.3s ease',
				border: isSelected ? '1px solid #E6D7C3' : '1px solid #E9ECEF',
				'&:hover': {
					backgroundColor: hoverBgColor,
					boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
				},
			}}
		>
			<CardActionArea
				onClick={handleClick}
				sx={{
					display: 'flex',
					justifyContent: 'flex-start',
					textAlign: 'left',
					padding: 2,
				}}
			>
				<Avatar
					src={mainCrop?.image_url}
					alt={mainCrop?.name}
					sx={{ width: 64, height: 64, borderRadius: '8px' }}
				/>

				<Box sx={{ ml: 2, flex: 1 }}>
					<Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5, fontSize: '1rem' }}>
						{mainCrop?.name || t('unknown')}
					</Typography>

					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							mb: 1,
							display: '-webkit-box',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{request.services?.name}
					</Typography>

					<Box
						sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}
					>
						<div className="flex gap-1 items-center">
							<Typography
								onClick={navigateToDetailedPage}
								variant="caption"
								sx={{
									color: '#00AB89',
									textTransform: 'uppercase',
									fontSize: '16px',
									fontWeight: 500,
									textDecoration: 'underline',
								}}
							>
								#{(request.request_id || request.id).toString().substring(0, 6)}
							</Typography>
						</div>

						<span className="text-secondary roboto-bold text-[16px] leading-[140%] max-sm:hidden">
							{request.offers_count || 0}
							<span className="ml-[2px]">{t('offers')}</span>
						</span>
					</Box>
				</Box>

				{isMobile && (
					<Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
						{isSelected ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</Box>
				)}
			</CardActionArea>
		</Card>
	);
};
